/**
 * Bounded wrapper for a Supabase auth call.
 *
 * `@supabase/auth-js` issues its `fetch` with **no timeout**. A stalled GoTrue
 * request — a token refresh that never comes back, a connection dropped
 * mid-flight — therefore hangs for as long as the tab is open. Awaiting one
 * behind a loading flag means the flag never clears, and because the promise
 * never settles there is no rejection: no `unhandledrejection`, nothing for a
 * global error handler to report, nothing in any server log. The screen simply
 * spins and the incident leaves no evidence.
 *
 * That is what happened to Hub's OAuth consent screen on 2026-09-06 (see
 * `apps/hub/docs/engineering-rules/MERIDIAN_MCP_OAUTH_TROUBLESHOOTING.md`,
 * rung 9): four consent page loads, one decision, zero errors anywhere.
 *
 * Wrap any auth call a user is waiting on. Build-guarded by
 * `scripts/validate-async-effect-completion.mjs`.
 */
/** Default budget. Generous — this is a backstop, not a latency target. */
export const AUTH_CALL_TIMEOUT_MS = 15000;
export class AuthCallTimeoutError extends Error {
    constructor(operation, ms) {
        super(`Supabase auth call "${operation}" did not respond within ${ms}ms`);
        /** Lets callers separate "took too long" from "failed" without instanceof. */
        this.isTimeout = true;
        this.name = "AuthCallTimeoutError";
        this.operation = operation;
    }
}
export function isAuthCallTimeout(error) {
    return error instanceof AuthCallTimeoutError;
}
/**
 * Runs `run()` and rejects with {@link AuthCallTimeoutError} if it has not
 * settled within `ms`.
 *
 * The underlying request is NOT cancelled — auth-js exposes no signal for it —
 * so this bounds what the CALLER waits for, which is the thing the user sees.
 *
 * @param operation Short label naming the call, e.g. `"getSession"`. It ends up
 *                  in the error message and therefore in the log, which is what
 *                  tells the next person which call stalled.
 */
export async function withAuthTimeout(operation, run, ms = AUTH_CALL_TIMEOUT_MS) {
    let timer;
    try {
        return await Promise.race([
            run(),
            new Promise((_resolve, reject) => {
                timer = setTimeout(() => reject(new AuthCallTimeoutError(operation, ms)), ms);
            }),
        ]);
    }
    finally {
        if (timer !== undefined)
            clearTimeout(timer);
    }
}
